import React, { useContext, useState } from "react";
import Loader from "react-loader-spinner";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";

import { Helper } from "../helper";
import { AuthContext } from "../context/auth";
import { showToast } from "utils/general";

const formatNaira = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  return `₦${value.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function Vas() {
  const context = useContext(AuthContext);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const admin = context.authState?.admin;
  const allowed =
    Helper.isSuper(admin) ||
    Helper.isAdministrator(admin) ||
    Helper.isFinance(admin);

  const fetchReport = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate || startDate > endDate) {
      showToast("Choose a start date on or before the end date");
      return;
    }

    const { token } = context.authState;
    setLoading(true);
    try {
      const response = await Helper.getData(
        `${process.env.REACT_APP_API_V2}/admin/vas/settlement?startDate=${startDate}&endDate=${endDate}`,
        token,
      );
      setReport(response?.data || null);
    } catch (error) {
      showToast("Could not load VAS settlement");
    } finally {
      setLoading(false);
    }
  };

  if (!allowed) {
    return (
      <div style={{ textAlign: "center" }}>
        <small>You are not permitted to view this page</small>
      </div>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          <Card>
            <Card.Header>
              <Card.Title as="h4">VAS</Card.Title>
              <p className="card-category">
                Successful airtime and data sales, and the amount to remit to
                VAS2Net
              </p>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={fetchReport}>
                <Row>
                  <Col md="3">
                    <Form.Group>
                      <Form.Label>Start date</Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group>
                      <Form.Label>End date</Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md="2">
                    <Form.Group>
                      <Form.Label className="d-block">&nbsp;</Form.Label>
                      <Button
                        type="submit"
                        className="btn-fill"
                        disabled={loading}
                      >
                        Load
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading && (
        <div className="text-center">
          <Loader type="ThreeDots" color="#212529" height={40} width={40} />
        </div>
      )}

      {report && !loading && (
        <>
          <Row>
            <Col md="6">
              <Card>
                <Card.Body>
                  <p className="card-category">Total spent</p>
                  <Card.Title as="h3">
                    {formatNaira(report.totalSpent)}
                  </Card.Title>
                </Card.Body>
              </Card>
            </Col>
            <Col md="6">
              <Card>
                <Card.Body>
                  <p className="card-category">Total to remit</p>
                  <Card.Title as="h3">
                    {formatNaira(report.totalRemit)}
                  </Card.Title>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Card>
                <Card.Body>
                  {(report.days || []).length === 0 ? (
                    <p className="card-category">
                      No successful sales in this range.
                    </p>
                  ) : (
                    (report.days || []).map((day) => (
                      <div key={day.day} className="mb-4">
                        <h5>
                          {day.day}{" "}
                          <small className="text-muted">
                            {formatNaira(day.spent)} spent,{" "}
                            {formatNaira(day.remit)} to remit
                          </small>
                        </h5>
                        <Table striped hover responsive>
                          <thead>
                            <tr>
                              <th>Biller</th>
                              <th>Transactions</th>
                              <th>Commission</th>
                              <th>Spent</th>
                              <th>To remit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(day.providers || []).map((provider) => (
                              <tr key={`${day.day}-${provider.provider}`}>
                                <td>{provider.provider}</td>
                                <td>{provider.count}</td>
                                <td>
                                  {provider.commission == null
                                    ? "—"
                                    : `${provider.commission}%`}
                                </td>
                                <td>{formatNaira(provider.spent)}</td>
                                <td>
                                  {provider.remit == null
                                    ? "—"
                                    : formatNaira(provider.remit)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default Vas;
